import React from 'react';
import { Image } from '../core/Image';
import Link from 'next/link';
import { getInternalPath, type IgetInternalPath } from '@helpers/internalLink';

// Correct type for the link field based on the Sanity schema
export type LinkField = {
  internalLink?: IgetInternalPath;
  externalUrl?: string;
};

type Card = {
  kop: string;
  subkop: string;
  image: any;
  link?: LinkField;
};

type ThreeCardsSectionProps = {
  card1: Card;
  card2: Card;
  card3: Card;
};

// White text on these card colors is low contrast (2.2:1 on the green,
// 1.4:1 on the yellow-green), so the text carries an explicit shadow.
// Tailwind's drop-shadow utilities are filters at 5-7% black, which is
// far too faint to help here.
const cardColors = [
  'bg-[#7AC143]', // green
  'bg-[#D6E800]', // yellow-green (middle card)
  'bg-[#7AC143]', // green
];

export default function ThreeCardsSection({ card1, card2, card3 }: ThreeCardsSectionProps) {
  const cards = [ card1, card2, card3 ];

  return (
    <section className='flex justify-center items-center py-6 md:py-10'>
      <div className='flex flex-col lg:flex-row lg:flex-wrap lg:justify-center items-center gap-6 lg:gap-8 bg-white rounded-[24px] lg:rounded-[40px] px-3 py-4 lg:px-10 lg:py-8 w-full max-w-6xl mx-auto'>
        {cards.map((card, idx) => {
          const cardClasses = `
            flex flex-col items-center w-full sm:w-[340px] md:flex-1 rounded-[32px] overflow-hidden ${ cardColors[idx] }
            relative aspect-[4/5] min-h-[300px] sm:min-h-[340px] md:min-h-[420px]
            transition-all duration-300 ease-in-out
            ${ card.link ? 'cursor-pointer hover:scale-105 hover:shadow-lg focus-within:scale-105 focus-within:shadow-lg' : '' }
          `;
          const cardInner = (
            <>
              <div className='w-full' style={ { height: '49%' } }>
                <div className='relative w-full h-full' style={ { minHeight: 0, height: '100%' } }>
                  <Image
                    sanityImage={ card.image.image }
                    layout='fill'
                    objectFit='cover'
                    className='w-full h-full'
                  />
                </div>
              </div>
              <div className='flex flex-col items-center justify-center flex-1 px-2 py-6 md:px-4 md:py-8 w-full gap-1'>
                <div className='text-white text-heading-md leading-tight text-center [text-shadow:0_1px_2px_rgba(0,0,0,0.3)] tracking-wide mb-1'>
                  {card.kop}
                </div>
                <div className='text-white text-body-md md:text-body-lg text-center [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]'>
                  {card.subkop}
                </div>
              </div>
            </>
          );
          return card.link && (card.link.internalLink || card.link.externalUrl) ? (
            <Link
              key={ idx }
              href={ card.link.externalUrl ?? (card.link.internalLink ? getInternalPath(card.link.internalLink) : '') }
              aria-label={ card.subkop }
              tabIndex={ 0 }
              className={ cardClasses }
            >
              {cardInner}
            </Link>
          ) : (
            <div key={ idx } className={ cardClasses } tabIndex={ -1 } aria-label={ card.subkop }>
              {cardInner}
            </div>
          );
        })}
      </div>
    </section>
  );
}
